import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import CaptainModal from '../../../Components/CaptainModal';
import {
    IAssesmentPayload,
    IOptionType,
    IScreenType,
} from '../helpers/typings';
import { v4 as uuidv4 } from 'uuid';
import OptionCard from '../OptionCard';
import OptionForm from './OptionForm';
import { cruAssessment } from '../helpers/utils';

const defaultOption: IOptionType = {
    id: '',
    value: '',
    rank: undefined,
    type: {
        category: 'mcq',
        subCategory: 'basic',
        selectionType: 'single',
    },
};

type AddScreenFormProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (payload: IAssesmentPayload, mode: 'create' | 'edit') => void;
    selectedAssessment: IAssesmentPayload;
    selectedScreen?: IScreenType;
};

const defaultQuestionScreen = (selectedScreen?: IScreenType): IScreenType => {
    if (selectedScreen?.type === 'question') return selectedScreen;

    const defaultScreen: IScreenType = {
        id: selectedScreen?.id || uuidv4(),
        description: selectedScreen?.description,
        type: 'question',
        rank: undefined,
        ismandatory: false,
        options: [],
    };

    return defaultScreen;
};

const defaultGenericScreen = (selectedScreen?: IScreenType): IScreenType => {
    if (selectedScreen?.type === 'generic') return selectedScreen;

    const defaultScreen: IScreenType = {
        id: selectedScreen?.id || uuidv4(),
        type: 'generic',
        description: selectedScreen?.description,
    };

    if (selectedScreen) {
        defaultScreen.id = selectedScreen.id;
        defaultScreen.description = selectedScreen.description;
    }

    return defaultScreen;
};

const AddScreenForm: React.FC<AddScreenFormProps> = ({
    isOpen,
    onClose,
    selectedScreen,
    selectedAssessment,
    onSuccess,
}) => {
    const [screenType, setScreenType] = useState<'question' | 'generic'>(
        selectedScreen?.type || 'question',
    );

    const [questionScreen, setQuestionScreen] = useState<IScreenType>(() =>
        defaultQuestionScreen(selectedScreen),
    );

    const [genericScreen, setGenericScreen] = useState<IScreenType>(() =>
        defaultGenericScreen(selectedScreen),
    );

    const [optionFormOpen, setOptionFormOpen] = useState(false);
    const [selectedOption, setSelectedOption] =
        useState<IOptionType>(defaultOption);

    const [screenPosition, setScreenPosition] = useState<number | undefined>(
        undefined,
    );

    const handleChange = (field: string, value: any) => {
        setQuestionScreen(prev => ({ ...prev, [field]: value }));
        if (field === 'description')
            setGenericScreen(prev => ({ ...prev, [field]: value }));
    };

    const addNewScreen = (screens: IScreenType[], newScreens: IScreenType) => {
        if (screenPosition === undefined || screenType === 'question')
            return [...screens, newScreens];
        const index = Number(screenPosition) - 1;
        return [
            ...screens.slice(0, index),
            newScreens,
            ...screens.slice(index),
        ];
    };
    const handleSubmit = (e: any) => {
        e.preventDefault();
        let screens = selectedAssessment.data.screens;
        if (selectedScreen) {
            if (screenType === 'generic' && screenPosition !== undefined) {
                /* To implement screen position change for question too ->
                 * Remove the screenType check from above & screenPosition input
                 */
                screens = screens.filter(
                    screen => screen.id !== selectedScreen.id,
                );
                screens = addNewScreen(screens, genericScreen);
            } else {
                const editedScreen =
                    screenType === 'question' ? questionScreen : genericScreen;
                screens = screens.map(screen =>
                    screen.id === selectedScreen.id ? editedScreen : screen,
                );
            }
        } else {
            screens = addNewScreen(
                screens,
                screenType === 'question' ? questionScreen : genericScreen,
            );
        }

        cruAssessment(
            {
                ...selectedAssessment,
                data: {
                    ...selectedAssessment.data,
                    screens,
                },
            },
            onSuccess,
        );
        onClose();
    };

    return (
        <CaptainModal isOpen={isOpen} onClose={onClose}>
            <div className="text-center fw-bold fs-4 mb-3">
                {selectedScreen ? 'Edit Screen' : 'Add Screen'}
            </div>

            <Form className="mb-3 mx-3" onSubmit={handleSubmit}>
                {/* Screen Type Selector */}
                <div className="mb-3">
                    <Form.Label>Screen Type:</Form.Label>
                    <Form.Select
                        value={screenType}
                        onChange={e =>
                            setScreenType(
                                e.target.value as 'question' | 'generic',
                            )
                        }
                        required
                    >
                        <option value="question">Question</option>
                        <option value="generic">Generic</option>
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>
                        Description
                        {screenType === 'question' ? ' (Optional)' : ''}:
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={questionScreen.description}
                        required={screenType === 'generic'}
                        onChange={e =>
                            handleChange('description', e.target.value)
                        }
                    />
                </div>

                {screenType === 'generic' && (
                    <div className="mb-3">
                        <Form.Label>Screen Position (Optional):</Form.Label>
                        <Form.Control
                            type="number"
                            value={screenPosition}
                            onChange={e =>
                                setScreenPosition(
                                    e.target.value === ''
                                        ? undefined
                                        : Number(e.target.value),
                                )
                            }
                        />
                    </div>
                )}

                {screenType === 'question' &&
                    questionScreen.type === 'question' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Header (Optional):</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={questionScreen.header}
                                    onChange={e =>
                                        handleChange('header', e.target.value)
                                    }
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Image URL (Optional):</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={questionScreen.image || ''}
                                    onChange={e =>
                                        handleChange('image', e.target.value)
                                    }
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Rank</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={questionScreen.rank}
                                    onChange={e =>
                                        handleChange(
                                            'rank',
                                            parseInt(e.target.value),
                                        )
                                    }
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Activity Time (Optional):
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    value={questionScreen.activityTime}
                                    onChange={e =>
                                        handleChange(
                                            'activityTime',
                                            parseInt(e.target.value),
                                        )
                                    }
                                />
                            </Form.Group>

                            <Form.Group className="mb-3 d-flex gap-2 align-items-center">
                                <Form.Check
                                    type="switch"
                                    id="ismandatory"
                                    label="Is Mandatory"
                                    checked={questionScreen.ismandatory}
                                    onChange={e =>
                                        handleChange(
                                            'ismandatory',
                                            e.target.checked,
                                        )
                                    }
                                />
                            </Form.Group>
                        </>
                    )}

                <Button type="submit" variant="primary" className="w-100">
                    {selectedScreen ? 'Save Changes' : 'Create Screen'}
                </Button>
            </Form>
            {screenType === 'question' &&
                questionScreen.type === 'question' && (
                    <OptionForm
                        isOpen={optionFormOpen}
                        onClose={() => {
                            setOptionFormOpen(false);
                            setSelectedOption(defaultOption);
                        }}
                        onSave={() => {
                            let newOption = true;
                            const screen: IScreenType = {
                                ...questionScreen,
                                options: questionScreen.options.map(opt => {
                                    if (opt.id === selectedOption.id) {
                                        newOption = false;
                                        return selectedOption;
                                    }
                                    return opt;
                                }),
                            };

                            !newOption && setQuestionScreen(screen);
                            newOption &&
                                setQuestionScreen({
                                    ...questionScreen,
                                    options: [
                                        ...questionScreen.options,
                                        selectedOption,
                                    ],
                                });
                            setSelectedOption(defaultOption);
                            setOptionFormOpen(false);
                        }}
                        option={selectedOption}
                        setOption={setSelectedOption}
                    />
                )}
            {screenType === 'question' &&
                questionScreen.type === 'question' && (
                    <>
                        <Button
                            onClick={() => setOptionFormOpen(true)}
                            variant="primary"
                            className="mb-3 mx-auto"
                            style={{ width: 'fit-content' }}
                        >
                            Add Option
                        </Button>

                        <div className="text-center fw-bold fs-4 mb-3">
                            Options
                        </div>
                        {questionScreen.options.length === 0 && (
                            <div className="text-center mb-3">
                                No options added yet
                            </div>
                        )}
                        {questionScreen.options.map(opt => (
                            <OptionCard
                                key={opt.id}
                                option={opt}
                                onEdit={() => {
                                    setSelectedOption(opt);
                                    setOptionFormOpen(true);
                                }}
                                onDelete={() => {
                                    const cnf = window.confirm(
                                        'Are you sure you want to delete this option?',
                                    );
                                    if (cnf) {
                                        const screen: IScreenType = {
                                            ...questionScreen,
                                            options:
                                                questionScreen.options.filter(
                                                    o => o.id !== opt.id,
                                                ),
                                        };
                                        setQuestionScreen(screen);
                                    }
                                }}
                            />
                        ))}
                    </>
                )}
        </CaptainModal>
    );
};

export default AddScreenForm;
